import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { AuthUser, LoginResponse, TokenPair } from '../types';
import { resetCsrfToken } from '../api/csrf';

const configured =
  typeof import.meta.env.VITE_API_BASE_URL === 'string'
    ? import.meta.env.VITE_API_BASE_URL.trim()
    : '';
const apiBaseUrl = configured.length > 0 ? configured : '/api';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null;
  user: AuthUser | null;
  loginError: string | null;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  setTokens: (tokens: TokenPair) => void;
  clearAuth: () => void;
}

/** Vérifie qu'un compte authentifié possède bien le rôle moniteur. */
export function isInstructor(user: AuthUser | null): boolean {
  return user?.role === 'instructor';
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      user: null,
      loginError: null,

      login: async (credentials) => {
        set({ loginError: null });
        // Le client axios brut évite la dépendance circulaire avec http.ts.
        const client = axios.create({
          baseURL: apiBaseUrl,
          withCredentials: true,
        });
        try {
          // CSRF requis sur les routes mutantes.
          const host = (() => {
            try {
              return new URL(apiBaseUrl).origin;
            } catch {
              return window.location.origin;
            }
          })();
          let csrf: string | null = null;
          try {
            const { data } = await axios.get<{ csrfToken?: string }>(
              `${host}/csrf-token`,
              { withCredentials: true },
            );
            csrf = data?.csrfToken ?? null;
          } catch {
            csrf = null;
          }
          const { data } = await client.post<LoginResponse>(
            '/v1/auth/login',
            credentials,
            { headers: csrf ? { 'x-csrf-token': csrf } : undefined },
          );
          if (data.user.role !== 'instructor') {
            set({
              loginError:
                "Ce compte n'est pas un compte moniteur. Utilisez l'application principale.",
            });
            return;
          }
          set({
            token: data.tokens.accessToken,
            refreshToken: data.tokens.refreshToken,
            tokenExpiresAt: Date.now() + data.tokens.expiresIn * 1000,
            user: data.user,
            loginError: null,
          });
        } catch (error) {
          const message =
            axios.isAxiosError(error) &&
            typeof error.response?.data === 'object' &&
            error.response?.data !== null
              ? ((error.response.data as Record<string, unknown>)
                  .message as string)
              : 'Identifiants invalides';
          set({ loginError: message ?? 'Identifiants invalides' });
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        try {
          if (refreshToken) {
            const client = axios.create({
              baseURL: apiBaseUrl,
              withCredentials: true,
            });
            await client.post(
              '/v1/auth/logout',
              { refreshToken },
              { headers: { Authorization: `Bearer ${get().token}` } },
            );
          }
        } catch {
          // best-effort
        } finally {
          get().clearAuth();
        }
      },

      setTokens: (tokens) =>
        set({
          token: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          tokenExpiresAt: Date.now() + tokens.expiresIn * 1000,
        }),

      clearAuth: () => {
        resetCsrfToken();
        set({
          token: null,
          refreshToken: null,
          tokenExpiresAt: null,
          user: null,
        });
      },
    }),
    {
      name: 'pmd-moniteur-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        user: state.user,
      }),
    },
  ),
);
